import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import MenuItemCard from '../components/MenuItemCard';
import {restaurantApi} from '../services/api';
import {useMerchant} from '../context/MerchantContext';
import {Category} from '../types';

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

interface ImageProps {
  image: string;
}

const RestaurantBanner = styled.div<ImageProps>`
  position: relative;
  width: 100%;
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 1rem;
  margin-top: 1.5rem;
  display: flex;
  align-items: flex-end;

  @media (max-width: 767px) {
    border-radius: 0;
    margin-top: 0;
  }
`;

const RestaurantLogo = styled.div<ImageProps>`
  width: 100px;
  height: 100px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: 50%;
  border: 4px solid white;
  position: absolute;
  bottom: -50px;
  left: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const RestaurantInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  margin-top: 2.5rem;
  margin-bottom: 2rem;
`;

const RestaurantName = styled.h1`
  font-size: 2rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const RestaurantDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 1rem;
`;

const CounterInfoContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid #e74c3c;
`;

const CounterTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const CounterInfo = styled.p`
  font-size: 1rem;
  color: #666;
`;

const OrderNumberContainer = styled.div`
  background-color: #e74c3c;
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const OrderNumberTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const OrderNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
`;

const MenuHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const MenuTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const MenuDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto;
`;

const CategoriesContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e74c3c;
    border-radius: 10px;
  }
`;

interface CategoryButtonProps {
  active: boolean;
}

const CategoryButton = styled.button<CategoryButtonProps>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 30px;
  background-color: ${props => props.active ? '#e74c3c' : '#f1f1f1'};
  color: ${props => props.active ? 'white' : '#333'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.active ? '#c0392b' : '#e0e0e0'};
  }
`;

const CategorySection = styled.section`
  margin-bottom: 3rem;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f1f1f1;
  padding-bottom: 0.5rem;
`;

const CategoryTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 1.5rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #e74c3c;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
`;

const RetryButton = styled.button`
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #c0392b;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 2rem;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #e74c3c;
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const CounterMenuPage: React.FC = () => {
  const { restaurantId: paramId } = useParams<{ restaurantId?: string }>();
  const { merchantId: contextId, restaurant: merchantRestaurant, isLoading: isMerchantLoading } = useMerchant();
  const restaurantId = paramId || contextId || 'default';
  
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const restaurant = merchantRestaurant;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);

  // Function to fetch menu data
  const fetchMenu = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await restaurantApi.getMenu(restaurantId);
      setCategories(data);
      if (data.length > 0 && !activeCategory) {
        setActiveCategory(data[0].id);
      }
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      setIsLoading(false);
    }
  }, [restaurantId, activeCategory]);

  // Refetch function
  const refetch = useCallback(() => {
    fetchMenu();
  }, [fetchMenu]);

  // Fetch data on component mount or when restaurantId changes
  useEffect(() => {
    fetchMenu();
    
    // Generate a random order number for demonstration purposes
    // In a real application, this would come from the backend
    setOrderNumber(Math.floor(Math.random() * 1000) + 1);
  }, [fetchMenu]);

  // Filter menu items based on search query
  const filteredCategories = categories?.map(category => ({
    ...category,
    items: category.items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  })).filter(category => category.items.length > 0);

  // Handle category change
  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setActiveCategory(null); // Clear active category when searching
  };

  if (isLoading || isMerchantLoading) {
    return (
      <Layout>
        <LoadingContainer>
          Carregando cardápio...
        </LoadingContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorContainer>
          <ErrorTitle>Ops! Algo deu errado.</ErrorTitle>
          <ErrorMessage>
            Não foi possível carregar o cardápio. Por favor, tente novamente mais tarde.
          </ErrorMessage>
          <RetryButton onClick={() => refetch()}>
            Tentar Novamente
          </RetryButton>
        </ErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout noContainer={true}>
      <div className="container-md px-0 px-md-4">
        {restaurant && (
          <RestaurantBanner image={restaurant.coverImage || 'https://via.placeholder.com/1200x400'}>
            <RestaurantLogo image={restaurant.logo || 'https://via.placeholder.com/150x150'} />
          </RestaurantBanner>
        )}
      </div>

      <div className="container-md px-md-4">
        <MenuContainer>
          {restaurant && (
            <>
              <RestaurantInfoContainer>
                <RestaurantName>{restaurant.name}</RestaurantName>
                <RestaurantDescription>{restaurant.description}</RestaurantDescription>
              </RestaurantInfoContainer>
              
              <CounterInfoContainer>
                <CounterTitle>Balcão de Atendimento</CounterTitle>
                <CounterInfo>
                  Faça seu pedido diretamente pelo cardápio digital. 
                  Nossos atendentes chamarão seu número quando o pedido estiver pronto.
                </CounterInfo>
              </CounterInfoContainer>

              {orderNumber && (
                <OrderNumberContainer>
                  <OrderNumberTitle>Seu número de pedido</OrderNumberTitle>
                  <OrderNumber>{orderNumber}</OrderNumber>
                </OrderNumberContainer>
              )}
            </>
          )}

        <MenuHeader>
          <MenuTitle>Nosso Cardápio</MenuTitle>
          <MenuDescription>
            Explore nosso cardápio completo e descubra opções deliciosas para satisfazer seu paladar.
          </MenuDescription>
        </MenuHeader>

        <SearchContainer>
          <SearchInput 
            type="text" 
            placeholder="Buscar no cardápio..." 
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <ClearSearchButton onClick={() => setSearchQuery('')}>
              <i className="fas fa-times" />
            </ClearSearchButton>
          )}
        </SearchContainer>

        {!searchQuery && (
          <CategoriesContainer>
            {categories?.map((category) => (
              <CategoryButton
                key={category.id}
                active={activeCategory === category.id}
                onClick={() => handleCategoryChange(category.id)}
              >
                {category.name}
              </CategoryButton>
            ))}
          </CategoriesContainer>
        )}

        {searchQuery && filteredCategories?.length === 0 ? (
          <ErrorContainer>
            <ErrorTitle>Nenhum resultado encontrado</ErrorTitle>
            <ErrorMessage>
              Não encontramos nenhum item que corresponda à sua busca. Tente outros termos.
            </ErrorMessage>
          </ErrorContainer>
        ) : searchQuery ? (
          // Show search results
          filteredCategories?.map((category) => (
            <CategorySection key={category.id}>
              <CategoryHeader>
                <CategoryTitle>{category.name}</CategoryTitle>
              </CategoryHeader>
              <ItemsGrid>
                {category.items.map((item) => (
                  <MenuItemCard 
                    key={item.id} 
                    item={item} 
                    stockControlEnabled={restaurant?.stockControlEnabled}
                  />
                ))}
              </ItemsGrid>
            </CategorySection>
          ))
        ) : (
          // Show active category or all categories
          categories?.map((category) => (
            (activeCategory === null || activeCategory === category.id) && (
              <CategorySection key={category.id}>
                <CategoryHeader>
                  <CategoryTitle>{category.name}</CategoryTitle>
                </CategoryHeader>
                <ItemsGrid>
                  {category.items.map((item) => (
                    <MenuItemCard 
                      key={item.id} 
                      item={item} 
                      stockControlEnabled={restaurant?.stockControlEnabled}
                    />
                  ))}
                </ItemsGrid>
              </CategorySection>
            )
          ))
        )}
        </MenuContainer>
      </div>
    </Layout>
  );
};

export default CounterMenuPage;